// src/services/offeredSubjectsService.ts
//
// The authoritative list of "subjects we offer" comes from the timetabling grid
// (the actual classes scheduled), NOT from curriculum_preferences (which is
// populated indirectly and is often incomplete). This service unions the class
// names across the grid(s) for a given academic year, deduped to one entry per
// subject, so the teaching-intentions form can show every subject on offer.
//
// Intentions run months before the target-year grid is finished, so the union
// spans the target year AND the most recent prior year — see `sourceYearsFor`.
// Issue #61: reading a single year meant the picker showed either none of the
// new-for-next-year subjects (before that grid existed) or only the handful on
// a part-built draft (after it was created).
//
// See docs/superpowers/specs/2026-06-12-offered-subjects-from-grid-design.md
import { gridsService } from './gridsService';
import { normalizeSubjectName } from '../utils/subjectName';

export interface OfferedSubject {
  id: string;
  name: string;
}

// Minimal shape we read from a grid — kept loose so the pure transform can be
// exercised with fixtures as well as real StaffAllocationGrid objects.
interface GridLike {
  name?: string;
  year?: number;
  classes?: Array<{ className?: string }>;
}

// Grids whose name contains "ruah" are excluded: RUAH subjects are nominated in
// a dedicated section of the intentions form, so listing them here would
// duplicate them.
const RUAH_GRID_PATTERN = /ruah/i;

// Names that are not teachable subjects and must never appear in the picker.
// Matched EXACTLY (case-insensitive) against the normalised name — never as a
// substring — so real subjects like "FOOD STUDIES" or "LEGAL STUDIES" are safe.
// Extend this list if other non-teaching lines surface on a grid.
const NON_TEACHING_LINES = new Set<string>([
  'STUDY',
  'PRIVATE STUDY',
  'STUDY HALL',
  'VCE STUDY',
  'SUPERVISION',
  'YARD',
  'YARD DUTY',
  'ASSEMBLY',
  'FORM ASSEMBLY',
  'HOMEROOM',
  'PASTORAL',
  'NO CLASS',
  'FREE',
  'RECESS',
  'LUNCH',
]);

function isTeachingSubject(name: string): boolean {
  return name.length > 0 && !NON_TEACHING_LINES.has(name.toUpperCase());
}

/**
 * Pick the grid years to read subjects from.
 *
 * Intentions for year N are collected while the year-N grid is still being
 * drafted, so neither year on its own describes what we offer:
 *
 * - Year N alone: while that grid is part-built it carries a fraction of the
 *   subjects, so most subjects cannot be nominated at all.
 * - Year N-1 alone: subjects introduced for year N are missing.
 *
 * So we read BOTH: the target year (when a grid for it exists) and the most
 * recent year before it. The result is a superset — a teacher may nominate a
 * subject that ends up not running, which a reviewer can resolve, whereas a
 * subject missing from the picker cannot be nominated at all. This matches the
 * deliberate bias of the exclusion list: when unsure, include.
 *
 * Returns the years newest-first, or an empty array when no grid exists on or
 * before `academicYear`.
 */
export function sourceYearsFor(grids: GridLike[], academicYear: number): number[] {
  const yearsOnOrBefore = Array.from(
    new Set(
      grids
        .map((g) => g.year)
        .filter((y): y is number => typeof y === 'number' && y <= academicYear)
    )
  ).sort((a, b) => b - a);

  if (yearsOnOrBefore.length === 0) return [];

  // The target year (when it has a grid) plus the most recent year before it.
  // When the target year has no grid yet this collapses to the previous-year
  // fallback the intentions form relied on before that grid was built.
  return yearsOnOrBefore.slice(0, yearsOnOrBefore[0] === academicYear ? 2 : 1);
}

/**
 * Pure transform: derive the deduped, sorted subject list from a set of grids.
 * Separated from the data load so it can be verified in isolation.
 *
 * - Excludes RUAH grids.
 * - Reads the target year's grid together with the most recent prior year's
 *   (see `sourceYearsFor`), so a part-built target-year grid never hides
 *   subjects and new-for-next-year subjects are still offered.
 * - Collapses the many classes of one subject to a single entry.
 */
export function subjectsFromGrids(grids: GridLike[], academicYear: number): OfferedSubject[] {
  const eligible = grids.filter((g) => !RUAH_GRID_PATTERN.test(g.name || ''));

  const sourceYears = new Set(sourceYearsFor(eligible, academicYear));
  if (sourceYears.size === 0) return [];

  const sourceGrids = eligible.filter(
    (g) => typeof g.year === 'number' && sourceYears.has(g.year)
  );

  const byName = new Map<string, string>();
  for (const grid of sourceGrids) {
    for (const cls of grid.classes || []) {
      if (!cls.className) continue;
      const name = normalizeSubjectName(cls.className);
      if (!isTeachingSubject(name)) continue;
      if (!byName.has(name)) byName.set(name, name);
    }
  }

  return Array.from(byName.keys())
    .map((name) => ({ id: name, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Load the subjects offered on the grid(s) for the given academic year.
 * Reads the target year's grid plus the most recent prior year's, so the list
 * stays complete while the target-year grid is still being built.
 * Returns an empty array if no grids exist on/before that year, or on error.
 */
export async function getOfferedSubjects(academicYear: number): Promise<OfferedSubject[]> {
  try {
    const grids = await gridsService.getAllGrids();
    return subjectsFromGrids(grids as GridLike[], academicYear);
  } catch (error) {
    console.error('Error loading offered subjects from grid:', error);
    return [];
  }
}
